import React, { useState, useEffect } from "react";
import { Form, Input, Button, Alert, Card, message } from "antd";
import { Lock } from "lucide-react";
import Logo from "../../assets/logo.png";
import video from "../../assets/5262343_Above_Aerial_Area_1920x1080.mp4";
import { useNavigate, useLocation } from "react-router-dom";
import { auth } from "../../config/firebase";
import { verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";

const ResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState(""); // Store the email for user feedback
  const [verifying, setVerifying] = useState(true); // Add state for initial verification
  const [resetComplete, setResetComplete] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();

  // Get the oobCode (action code) from the URL
  const queryParams = new URLSearchParams(location.search);
  const actionCode = queryParams.get("oobCode");

  // Verify the reset code on component mount
  useEffect(() => {
    const verifyCode = async () => {
      if (!actionCode) {
        navigate('/');
        return;
      }

      try {
        setVerifying(true);
        // Verify the password reset code
        const emailFromCode = await verifyPasswordResetCode(auth, actionCode);
        setEmail(emailFromCode);
        setError("");
      } catch (err) {
        console.error("Error verifying reset code:", err);
        setError("This password reset link has expired or is invalid.");
      } finally {
        setVerifying(false);
      }
    };

    verifyCode();
  }, [actionCode, navigate]);

  const onFinish = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      setLoading(true);
      setError("");

      // Confirm password reset with Firebase
      await confirmPasswordReset(auth, actionCode, values.newPassword);
      // The action code is consumed now. Keep showing a success state while
      // the confirmation message is displayed before redirecting.
      setResetComplete(true);

      message.success('Password Reset Successfully');
      // Remove the consumed action-code URL so the page cannot remain on the
      // now-invalid reset link.
      setTimeout(() => {
        window.location.replace("/");
      }, 1000);
    } catch (err) {
      console.error("Error resetting password:", err);
      setError(err.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const validateConfirmPassword = ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value || getFieldValue("newPassword") === value) {
        return Promise.resolve();
      }
      return Promise.reject(new Error("Passwords do not match!"));
    },
  });

  // If no oobCode is present, component will redirect before rendering
  if (!actionCode) {
    return null;
  }

  // Show loading state while verifying code
  if (verifying) {
    return (
      <div className="flex min-h-screen justify-center items-center">
        <div className="text-center">
          <p className="text-lg">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  if (resetComplete) {
    return (
      <div className="flex min-h-screen justify-center items-center">
        <div className="text-center">
          <p className="text-lg">Password reset successfully. Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <div className="lg:w-1/2 hidden lg:block relative">
        <video autoPlay loop muted className="absolute inset-0 w-full h-full object-cover">
          <source src={video} />
        </video>
      </div>

      <div className="lg:w-1/2 w-full bg-white p-8 flex flex-col justify-center items-center">
        <img src={Logo} alt="Logo" className="w-1/5 mb-8" />

        <Card className="w-full max-w-md shadow-none">
          <div className="text-center mb-6">
            <Lock className="w-12 h-12 mx-auto mb-4 text-blue-600" />
            <h1 className="text-2xl font-bold">Reset Password</h1>
            <p className="text-gray-600">
              {email ? `Please enter a new password for ${email}` : "Please enter your new password"}
            </p>
          </div>

          {error && (
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              className="mb-4"
            />
          )}

          {!error ? (
            <Form
              form={form}
              name="passwordReset"
              onFinish={onFinish}
              layout="vertical"
            >
              <Form.Item
                name="newPassword"
                label="New Password"
                rules={[
                  { required: true, message: "Please enter your new password!" },
                  { min: 6, message: "Password must be at least 6 characters!" },
                ]}
              >
                <Input.Password
                  size="large"
                  placeholder="New Password"
                  className="h-12"
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirm Password"
                dependencies={["newPassword"]}
                rules={[
                  { required: true, message: "Please confirm your password!" },
                  validateConfirmPassword,
                ]}
              >
                <Input.Password
                  size="large"
                  placeholder="Confirm Password"
                  className="h-12"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 h-12"
                >
                  Reset Password
                </Button>
              </Form.Item>
            </Form>
          ) : (
            <div className="text-center mt-4">
              <Button
                type="primary"
                onClick={() => navigate("/")}
                style={{ backgroundColor: "#2563eb", borderColor: "#2563eb", color: "#ffffff" }}
              >
                Back to Login
              </Button>
            </div>
          )}

          {!error && (
            <div className="text-center mt-4">
              <a href="/" className="text-blue-600 hover:text-blue-800">
                Back to Login
              </a>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
